import { useState } from "react";
import { Reveal } from "./Reveal";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useContent } from "@/context/ContentContext";

const emptyForm = { name: "", email: "", company: "", service: "", message: "", website: "" };
const fieldClass =
  "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-white/30 focus:border-gold outline-none transition-colors duration-300";

export const Contact = () => {
  const c = useContent().contact;
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("ご相談内容を選択してください");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("お問い合わせを送信しました。ありがとうございます。");
      setForm(emptyForm);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-ink py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-gold mb-6">{c.eyebrow}</p>
            <h2 className="font-jp text-4xl sm:text-5xl font-bold tracking-tight chrome-text leading-tight whitespace-pre-line">
              {c.heading}
            </h2>
            <p className="mt-8 text-base font-light leading-relaxed text-white/60 max-w-sm">{c.description}</p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input data-testid="contact-name" className={fieldClass} placeholder="お名前 *" value={form.name} onChange={set("name")} required />
                <input data-testid="contact-email" type="email" className={fieldClass} placeholder="メールアドレス *" value={form.email} onChange={set("email")} required />
              </div>
              <input data-testid="contact-company" className={fieldClass} placeholder="会社名（任意）" value={form.company} onChange={set("company")} />

              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="contact-service" className="w-full bg-transparent border-0 border-b border-white/20 rounded-none py-3 h-auto text-white focus:border-gold focus:ring-0 px-0">
                  <SelectValue placeholder="ご相談内容 *" />
                </SelectTrigger>
                <SelectContent>
                  {c.services.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`service-opt-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <textarea data-testid="contact-message" rows={4} className={`${fieldClass} resize-none`} placeholder="メッセージ *" value={form.message} onChange={set("message")} required />

              <button type="submit" data-testid="contact-submit" disabled={loading} className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-medium text-ink hover:bg-gold-light glow-cyan transition-colors duration-300 disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                送信する
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
